import {RuleHumanID, RuleHumanIdEnum} from './rule-info.enum';

//#region INTERFACE IRuleInfoRaw
/**
 * ### IRuleInfoRaw
 * Represents raw rule information in the system, including details such as access level,
 * dependencies, descriptions, and nested rules.
 *
 * @interface IRuleInfoRaw
 * @description This interface is used to define raw rule data with specific properties such as a human-readable ID,
 * text description, access level, and potential dependencies. It is meant for representing rule structures
 * before they are processed or resolved into more usable formats in the application.
 *
 * @example
 * const example: IRuleInfoRaw = {
 *   humanID: 'a.',
 *   text: 'Edit Staff Access',
 *   accessCode: 1,
 *   depend: ['b.'],
 *   desc: 'This rule allows staff members to edit access controls.',
 *   toolTip: 'Click for more details.'
 * };
 */
export interface IRuleInfoRaw {
  /**
   * The unique human-readable ID for the rule.
   * @description Each rule is assigned a unique identifier used throughout the system.
   *               This ID is typically a string that represents the rule in a human-readable format.
   *
   * @type {RuleHumanID}
   * @example 'a.'
   */
  humanID: RuleHumanID;

  /**
   * The text of the rule.
   * @description This property describes the rule in text, explaining its purpose and any actions or restrictions it imposes.
   *
   * @type {string}
   * @example 'Rule 1: Do not violate policy.'
   */
  text: string;

  /**
   * The access code that defines the priority or level of the rule.
   * @description This is an optional property that, if provided, indicates the rule's access level or priority.
   *              Values range from 0 to 13, with each number representing a different level.
   *
   * @type {0| 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 16 | 17}
   * @optional
   * @example 1
   */
  accessCode?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 16 | 17;

  /**
   * An array of rule IDs that this rule depends on.
   * @description This optional property lists other rules that must be fulfilled for this rule to apply.
   *               It represents a hierarchical structure of dependencies.
   *
   * @type {RuleHumanID[]}
   * @optional
   * @example ['b.', 'c.']
   */
  depend?: RuleHumanID[];

  /**
   * An optional description for the rule providing additional context.
   * @description This property can explain the purpose, restrictions, or context in more detail.
   *               It's especially helpful for providing guidance on what the rule affects.
   *
   * @type {string}
   * @optional
   * @example 'This rule applies to all users with administrative privileges.'
   */
  desc?: string;

  /**
   * An optional array of nested rules related to the rule.
   * @description This property defines any sub-rules associated with the current rule. It represents
   *               a hierarchy of rules, allowing more detailed rule management.
   *
   * @type {IRuleInfoRaw[]}
   * @optional
   * @example [{ humanID: 'b.i', text: 'Access Stock Edit Interface' }]
   */
  rules?: IRuleInfoRaw[];

  /**
   * An optional tooltip for the rule, typically used for UI tooltips.
   * @description This property provides additional information when the user hovers over the rule in the UI.
   *
   * @type {string}
   * @optional
   * @example 'Click for more details.'
   */
  toolTip?: string;
}

//#endregion

//#region INTERFACE IRuleInfo

/**
 * ### IRuleInfo
 * Represents processed rule information, including resolved dependencies, nested rules, and other properties.
 *
 * @interface IRuleInfo
 * @description This interface represents a fully-processed version of a rule, with resolved dependencies,
 *              nested rules, and structured data ready for display or application logic.
 *
 * @example
 * const example: IRuleInfo = {
 *   depend: [{ humanID: 'b.', text: 'View / Search Stock Items', index: 0, rules: [] }],
 *   humanID: 'a.',
 *   index: 0,
 *   rules: [],
 *   text: 'Edit Staff Access'
 * };
 */
export interface IRuleInfo {
  /**
   * An array of fully-resolved rule objects this rule depends on.
   * @description This property contains other rules that need to be resolved and applied before
   *               this rule can take effect. These dependencies are fully processed rule objects.
   *
   * @type {IRuleInfo[]}
   * @example [{ humanID: 'b.', text: 'View / Search Stock Items', index: 0, rules: [] }]
   */
  depend: IRuleInfo[];

  /**
   * The unique human-readable ID for the rule.
   * @description This ID uniquely identifies the rule within the system.
   *
   * @type {RuleHumanID}
   * @example 'a.'
   */
  humanID: RuleHumanID;

  /**
   * The index of the rule in the rule list.
   * @description This property represents the position of the rule in a list or hierarchy of rules.
   *
   * @type {number}
   * @example 0
   */
  index: number;

  /**
   * An array of fully-resolved nested rules related to the rule.
   * @description This array contains rules that are directly nested within the current rule. These are fully
   *              processed and ready for use.
   *
   * @type {IRuleInfo[]}
   * @example [{ humanID: 'b.i', text: 'Access Stock Edit Interface' }]
   */
  rules: IRuleInfo[];

  /**
   * The text of the rule.
   * @description This property provides a description or title for the rule, explaining its purpose or constraints.
   *
   * @type {string}
   * @example 'Edit Staff Access'
   */
  text: string;

  /**
   * The access bit position for this rule in the store access code bitmask.
   * @description When omitted, the rule index is used as the bit position.
   *
   * @type {number}
   * @optional
   * @example 16
   */
  accessCode?: number;

  /**
   * An optional description for the rule.
   * @description This property provides additional context, such as explaining the scope of the rule or its impact.
   *
   * @type {string}
   * @optional
   * @example 'This rule applies to all users with administrative privileges.'
   */
  desc?: string;

  /**
   * An optional tooltip for the rule.
   * @description This property is typically used to provide more detailed information in a UI element when the rule is hovered over.
   *
   * @type {string}
   * @optional
   * @example 'Click for more details.'
   */
  toolTip?: string;


}

//#endregion

//#region CONST rawRuleInfoRawStructure

/**
 * ### RawRuleInfoRawStructure
 * A constant array representing the structure of raw rule information.
 *
 * @constant
 * @type {IRuleInfoRaw[]}
 * @description This constant contains definitions of various rules, including their access level, human-readable
 *              ID, text, dependencies, and possible nested rules.
 *
 * @example
 * const rules: IRuleInfoRaw[] = RawRuleInfoRawStructure;
 */
export const RawRuleInfoRawStructure: IRuleInfoRaw[] = [
  {
    accessCode: 0,
    humanID: RuleHumanIdEnum['a.'],
    text: 'Edit Staff Access',
  },
  {
    accessCode: 1,
    humanID: RuleHumanIdEnum['b.'],
    text: 'View / Search Stock Items',
    rules: [
      {
        accessCode: 2,
        humanID: RuleHumanIdEnum['b.i'],
        text: 'Access Stock Edit Interface',
      },
      {
        accessCode: 3,
        humanID: RuleHumanIdEnum['b.ii'],
        text: 'Submit Stock Edits to IQ',
      },
    ],
  },
  {
    accessCode: 4,
    depend: [
      RuleHumanIdEnum['b.i'],
      RuleHumanIdEnum['b.ii'],
    ],
    humanID: RuleHumanIdEnum['c.'],
    text: 'View NGP Report',
  },
  {
    accessCode: 5,
    humanID: RuleHumanIdEnum['d.'],
    text: 'Access Auto Ordering Tool',
    rules: [
      {
        accessCode: 6,
        /** @deprecated Legacy — not assignable via Customise Permissions UI */
        text: 'See Colleagues Orders',
        humanID: RuleHumanIdEnum['d.i'],
      },
      {
        accessCode: 7,
        /** @deprecated Legacy — not assignable via Customise Permissions UI */
        humanID: RuleHumanIdEnum['d.ii'],
        text: 'Edit Colleagues Orders',
      },
      {
        accessCode: 8,
        humanID: RuleHumanIdEnum['d.iii'],
        text: 'Submit Auto Orders',
      },
      {
        accessCode: 9,
        humanID: RuleHumanIdEnum['d.iv'],
        text: 'Edit Schedule',
      },
      {
        accessCode: 10,
        humanID: RuleHumanIdEnum['d.v'],
        text: 'Run Unscheduled Orders',
      },
      {
        accessCode: 11,
        humanID: RuleHumanIdEnum['d.vi'],
        text: 'View & Edit Settings',
      },
    ],
  },
  {
    accessCode: 12,
    humanID: RuleHumanIdEnum['e.'],
    text: 'View Dashboard',
  },
  {
    accessCode: 13,
    humanID: RuleHumanIdEnum['f.'],
    text: 'View Shelf Talkers',
  },
  {
    accessCode: 14,
    humanID: RuleHumanIdEnum['g.'],
    text: 'Barcode Scanner',
  },
  {
    accessCode: 16,
    humanID: RuleHumanIdEnum['i.'],
    text: 'Stock take - Can count / edit stock takes (not process or delete)',
  },
  {
    accessCode: 17,
    humanID: RuleHumanIdEnum['j.'],
    text: 'Stock take - Can process stock takes on server, delete stock takes, edit schedule',
  },
];

//#endregion
